import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import {
  TextField,
  Button,
  Alert,
  Stack,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';

const sections = [
  {
    title: null,
    questions: [
      { key: 'company_name', label: 'Company Name', type: 'text', bant: 'N', service: [] },
      { key: 'industry_sector', label: 'Industry/Sector', type: 'text', bant: 'N', service: [] },
      { key: 'organisation_size', label: 'Size of Organisation (number of employees)', type: 'text', bant: 'N', service: [] },
      { key: 'main_products', label: 'Main Products/Services', type: 'text', bant: 'N', service: [] },
      { key: 'name_role', label: 'Name and Role of Interviewee', type: 'text', bant: 'A', service: [] },
      { key: 'email', label: 'Email Address', type: 'email', bant: 'A', service: [] },
      {
        key: 'internal_sponsor',
        label: 'Do you have an internal sponsor to champion and approve AI projects?',
        type: 'select',
        bant: 'A',
        service: ['C'],
        options: [
          "I don’t need one as I am the decision maker",
          'Yes, I have one or more executive sponsors already on board',
          'Not yet, but I’m in the process of identifying one',
          'No, I will need to secure sponsorship before moving forward',
          'Unsure / Other (please specify)',
        ],
      },
    ],
  },
  {
    title: 'Section 1: Business Objectives & Strategic Priorities',
    questions: [
      {
        key: 'strategic_goals',
        label: 'What are your company’s top 3 strategic goals for the next 12–24 months?',
        type: 'text',
        bant: 'N',
        service: ['R', 'C'],
      },
      {
        key: 'improve_efficiency',
        label:
          "Are there any particular areas where you're aiming to improve efficiency, reduce costs, or grow revenue?",
        type: 'text',
        bant: 'N',
        service: ['R', 'C'],
      },
      {
        key: 'digital_transformation',
        label: 'Are you currently exploring or investing in digital transformation initiatives?',
        type: 'select',
        bant: 'N',
        service: ['R', 'C'],
        options: ['Yes', 'No', 'Planning one'],
      },
      {
        key: 'data_ai_budget',
        label: 'Do you have a dedicated budget associated to data and/or AI?',
        type: 'select',
        bant: 'B',
        service: ['R', 'P', 'C'],
        options: ['Up to £25k', '£25k - £75k', '£75k - £150k', '£150k upwards'],
      },
    ],
  },
  {
    title: 'Section 2: Current Use of Data & Analytics',
    questions: [
      {
        key: 'data_use',
        label:
          'How would you describe your organisation’s current use of data in decision-making?',
        type: 'select',
        bant: 'N',
        service: ['R'],
        options: [
          'Mostly gut-driven',
          'Some data-driven processes',
          'Regular use of analytics',
          'Advanced analytics & automation',
        ],
      },
      {
        key: 'data_types',
        label: 'What types of data do you currently collect and store?',
        type: 'text',
        bant: 'N',
        service: ['R'],
      },
      {
        key: 'data_storage',
        label: 'Where is your data primarily stored?',
        type: 'select',
        bant: 'N',
        service: ['R'],
        options: [
          'Cloud (e.g., AWS, Azure, GCP)',
          'On-premise',
          'Hybrid',
          'Not sure',
        ],
      },
      {
        key: 'data_quality',
        label: 'How would you rate your data quality?',
        type: 'select',
        bant: 'N',
        service: ['R'],
        options: ['1', '2', '3', '4', '5'],
      },
      {
        key: 'data_warehouse',
        label: 'Do you have a central data warehouse or data lake?',
        type: 'select',
        bant: 'N',
        service: ['R'],
        options: ['Yes', 'No'],
      },
      {
        key: 'data_accessibility',
        label: 'How accessible is data across departments (marketing, operations, finance, etc.)?',
        type: 'text',
        bant: 'N',
        service: ['R'],
      },
      {
        key: 'bi_tools',
        label: 'Do you currently use BI tools or dashboards? If so, which ones?',
        type: 'select',
        bant: 'N',
        service: ['R'],
        options: ['Power BI', 'Tableau', 'Looker', 'Other'],
      },
    ],
  },
  {
    title: 'Section 3: AI & Automation Maturity',
    questions: [
      {
        key: 'ai_implemented',
        label: 'Have you implemented any AI or machine learning solutions already?',
        type: 'select',
        bant: 'N',
        service: ['R', 'C'],
        options: ['Yes', 'No', 'In experimentation phase'],
      },
      {
        key: 'ai_initiatives',
        label: 'If yes, please describe current or past AI initiatives and their outcomes.',
        type: 'text',
        bant: 'N',
        service: ['R', 'C'],
      },
      {
        key: 'ai_interest',
        label: 'What types of automation or AI are you most interested in?',
        type: 'multiselect',
        bant: 'N',
        service: ['R', 'C'],
        options: [
          'Predictive analytics',
          'Process automation',
          'Natural language processing/chatbots',
          'Computer vision',
          'Recommendation systems',
          'Anomaly/fraud detection',
          'Generative AI (text/image/code generation)',
          'Other',
        ],
      },
      {
        key: 'data_science_team',
        label: 'Do you have internal data science or AI teams?',
        type: 'select',
        bant: 'A',
        service: ['R', 'C'],
        options: ['Yes', 'No', 'Planning to hire/build one'],
      },
      {
        key: 'tech_tools',
        label: 'What programming languages, platforms, or tools does your technical team use (if any)?',
        type: 'text',
        bant: 'N',
        service: ['R'],
      },
      {
        key: 'ai_policy',
        label: 'Do you have an AI / data-privacy policy?',
        type: 'select',
        bant: 'N',
        service: ['P'],
        options: ['Yes', 'No', 'Planning to'],
      },
      {
        key: 'compliance_issues',
        label: 'Have you experienced any compliance or audit issues related to AI?',
        type: 'select',
        bant: 'N',
        service: ['P'],
        options: ['Yes', 'No'],
      },
    ],
  },
  {
    title: 'Section 4: Technical Infrastructure & Resources',
    questions: [
      {
        key: 'leadership_support',
        label: 'What level of support does leadership provide for AI initiatives?',
        type: 'select',
        bant: 'A',
        service: ['R', 'P', 'C'],
        options: [
          'Strong championing',
          'Moderate interest',
          'Limited engagement',
          'Not yet a priority',
        ],
      },
      {
        key: 'technical_challenges',
        label: 'What are your key technical challenges related to AI adoption?',
        type: 'text',
        bant: 'N',
        service: ['R', 'C'],
      },
      {
        key: 'data_strategy',
        label: 'Do you have an existing AI or data strategy?',
        type: 'select',
        bant: 'N',
        service: ['R'],
        options: ['Yes', 'No'],
      },
      {
        key: 'roi_evaluation',
        label: 'How do you currently evaluate the success or ROI of digital or AI projects?',
        type: 'text',
        bant: 'N',
        service: ['C'],
      },
    ],
  },
  {
    title: 'Section 5: AI Opportunities & Use Case Identification',
    questions: [
      {
        key: 'ai_opportunities',
        label: 'What business areas do you believe could benefit from AI or automation?',
        type: 'multiselect',
        bant: 'N',
        service: ['C'],
        options: [
          'Marketing & Sales',
          'Customer Service',
          'Operations / Supply Chain',
          'HR / Talent Acquisition',
          'Finance / Risk',
          'Product / R&D',
          'Compliance / Legal',
          'Other',
        ],
      },
      {
        key: 'tasks_automatable',
        label:
          'Are there repetitive, manual, or high-volume tasks in your organisation that could be automated?',
        type: 'select',
        bant: 'N',
        service: ['C'],
        options: ['Yes', 'No'],
      },
      {
        key: 'decisions_predictions',
        label: 'Are there decisions that could be improved with better predictions or insights?',
        type: 'select',
        bant: 'N',
        service: ['C'],
        options: ['Yes', 'No'],
      },
      {
        key: 'open_to_codevelop',
        label: 'Are you open to co-developing or piloting AI use cases with a consultancy?',
        type: 'select',
        bant: 'A',
        service: ['C'],
        options: ['Yes', 'No', 'Unsure'],
      },
      {
        key: 'success_definition',
        label: 'What would success look like for your organisation in working with an AI consultancy?',
        type: 'text',
        bant: 'N',
        service: ['R', 'P', 'C'],
      },
      {
        key: 'initiative_timing',
        label: 'How soon are you looking to begin or scale your AI initiatives?',
        type: 'text',
        bant: 'T',
        service: ['R', 'P', 'C'],
      },
      {
        key: 'follow_up_person',
        label: 'Who would be the best person(s) to involve in follow-up discussions?',
        type: 'text',
        bant: 'A',
        service: ['R', 'P', 'C'],
      },
    ],
  },
];

export default function FreeAIAssessmentForm() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const totalQuestions = sections.reduce(
    (acc, s) => acc + s.questions.length,
    0
  );
  const answered = sections.reduce((acc, s) => {
    return (
      acc +
      s.questions.filter((q) => {
        const val = values[q.key];
        if (Array.isArray(val)) {
          return val.length > 0;
        }
        return !!val;
      }).length
    );
  }, 0);
  const progress = Math.round((answered / totalQuestions) * 100);

  useEffect(() => {
    return () => {
      if (!submitted) {
        trackEvent('ai_assessment_progress', { progress });
      }
    };
  }, [submitted, progress]);

  const handleChange = (key) => (event) => {
    setValues((v) => ({ ...v, [key]: event.target.value }));
  };

  const handleCheckboxChange = (key, option) => (event) => {
    const checked = event.target.checked;
    setValues((v) => {
      const arr = v[key] || [];
      if (checked) {
        return { ...v, [key]: [...arr, option] };
      }
      return { ...v, [key]: arr.filter((o) => o !== option) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!values.name_role || !values.email) {
      setError('Name and email are required');
      return;
    }

    setLoading(true);
    const responses = sections.flatMap((s) =>
      s.questions.map((q) => ({
        question: q.label,
        answer: Array.isArray(values[q.key]) ? values[q.key].join(', ') : values[q.key] || '',
        bant: q.bant || '',
        service: q.service || [],
      }))
    );

    try {
      const res = await fetch('/api/questionnaire-responses/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name_role,
          email: values.email,
          responses,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          data.detail ||
          Object.values(data)
            .flat()
            .join(' ');
        throw new Error(msg || 'Failed to submit');
      }
      setValues({});
      setSubmitted(true);
      trackEvent('ai_assessment_complete', {});
      navigate('/services/free-ai-assessment/thank-you');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Stack spacing={2} sx={{ color: 'common.white' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {sections.map((section) => (
          <React.Fragment key={section.title || 'root'}>
            {section.title && (
              <Typography variant="h6" sx={{ color: 'common.white' }}>
                {section.title}
              </Typography>
            )}
            {section.questions.map((q) => {
              if (q.type === 'multiselect') {
                const arr = values[q.key] || [];
                return (
                  <FormGroup key={q.key}>
                    <Typography sx={{ color: 'common.white' }}>{q.label}</Typography>
                    {q.options.map((opt) => (
                      <FormControlLabel
                        key={opt}
                        control={
                          <Checkbox
                            checked={arr.includes(opt)}
                            onChange={handleCheckboxChange(q.key, opt)}
                          />
                        }
                        label={opt}
                        sx={{ color: 'common.white' }}
                      />
                    ))}
                  </FormGroup>
                );
              }
              if (q.type === 'select') {
                return (
                  <TextField
                    key={q.key}
                    select
                    label={q.label}
                    value={values[q.key] || ''}
                    onChange={handleChange(q.key)}
                    InputProps={{ sx: { backgroundColor: '#fff', color: '#182D46' } }}
                    InputLabelProps={{
                      sx: {
                        color: '#182D46',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -2px) scale(0.75)',
                      },
                      },
                    }}
                  >
                    {q.options.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              }
              return (
                <TextField
                  key={q.key}
                  label={q.label}
                  value={values[q.key] || ''}
                  onChange={handleChange(q.key)}
                  type={q.type === 'text' ? undefined : q.type}
                  multiline={q.type === 'text'}
                  InputProps={{ sx: { backgroundColor: '#fff', color: '#182D46' } }}
                  InputLabelProps={{
                    sx: {
                      color: '#182D46',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -2px) scale(0.75)',
                      },
                    },
                  }}
                />
              );
            })}
          </React.Fragment>
        ))}
        <Button type="submit" variant="contained" disabled={loading}>
          Submit
        </Button>
      </Stack>
    </form>
  );
}

